import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Container,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Chip,
  Divider,
  Avatar,
  Tabs,
  Tab,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonOffIcon from '@mui/icons-material/PersonOff';

import LoadingButton from '@mui/lab/LoadingButton';
import { useQueryClient } from 'react-query';
import { format } from 'date-fns';
import { getUserDetails } from '../../utils';
import { useQueryGetSideLeaderRequests, useMutationUpdateSiteLeaderRequest } from '../../hooks/react-query/site-leader';
import EmptyState from '../../components/empty-state';

const SiteLeaderRequests = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userDetails = getUserDetails();

  const [payload, setPayload] = useState({});
  const [selectedTab, setSelectedTab] = useState(0); // 0: Pending, 1: Accepted, 2: Rejected

  const { data: sideLeaderRequests, isFetching: isLoading } = useQueryGetSideLeaderRequests(
    userDetails?.token,
    navigate,
    toast
  );

  const { isLoading: isSiteLeaderRequestUpdating, mutate: updateUserRequest } = useMutationUpdateSiteLeaderRequest(
    payload,
    toast,
    userDetails?.token,
    navigate,
    setPayload,
    queryClient
  );

  useEffect(() => {
    if (Object.keys(payload).length > 0) {
      updateUserRequest();
    }
  }, [payload]);

  const allRequests = sideLeaderRequests?.data?.data || [];
  
  // Filter requests based on selected tab and sort by timestamp (newest first)
  const getFilteredRequests = () => {
    let filtered = [];
    if (selectedTab === 0) filtered = allRequests.filter(req => req.status === 'PENDING');
    else if (selectedTab === 1) filtered = allRequests.filter(req => req.status === 'ACCEPTED');
    else if (selectedTab === 2) filtered = allRequests.filter(req => req.status === 'REJECTED');
    
    // Sort by timestamp in descending order (newest first)
    return filtered.sort((a, b) => {
      const timestampA = a?.timestamp || a?.createdAt || a?.created_at || 0;
      const timestampB = b?.timestamp || b?.createdAt || b?.created_at || 0;
      return new Date(timestampB) - new Date(timestampA);
    });
  };

  const filteredRequests = getFilteredRequests();
  const hasRequests = Array.isArray(allRequests) && allRequests.length > 0;
  const hasFilteredRequests = Array.isArray(filteredRequests) && filteredRequests.length > 0;

  // Get counts for each status
  const pendingCount = allRequests.filter(req => req.status === 'PENDING').length;
  const acceptedCount = allRequests.filter(req => req.status === 'ACCEPTED').length;
  const rejectedCount = allRequests.filter(req => req.status === 'REJECTED').length;

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  return (
    <Box sx={{ py: { xs: 2, md: 4 } }}>
      <Helmet>
        <title>Edifying Teachers | Edifiers</title>
      </Helmet>

      <Container maxWidth="xl" sx={{ p: '0 !important' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 4,
        }}
      >
        <Box>
            <Typography variant="h3" sx={{ mb: '5px', color: 'text.titles' }}>
            Pairing Requests
          </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mt: 1, maxWidth: 480 }}>
            Review and respond to pairing requests from Teachers. Approvals immediately notify the requesting team.
          </Typography>
          </Box>
        </Box>

        {/* Status Tabs */}
        {!isLoading && hasRequests && (
          <Box sx={{ mb: 3 }}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  minHeight: 48,
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    color: 'text.primary',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: 'primary.main',
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                },
              }}
            >
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Pending
                    {pendingCount > 0 && (
                      <Chip
                        label={pendingCount}
                        size="small"
                        sx={{
                          height: 20,
                          minWidth: 20,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          backgroundColor: 'warning.lighter',
                          color: 'warning.dark',
                        }}
                      />
                    )}
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Accepted
                    {acceptedCount > 0 && (
                      <Chip
                        label={acceptedCount}
                        size="small"
                        sx={{
                          height: 20,
                          minWidth: 20,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          backgroundColor: 'success.lighter',
                          color: 'success.dark',
                        }}
                      />
                    )}
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Rejected
                    {rejectedCount > 0 && (
        <Chip
                        label={rejectedCount}
                        size="small"
          sx={{
                          height: 20,
                          minWidth: 20,
                          fontSize: '0.75rem',
            fontWeight: 600,
                          backgroundColor: 'error.lighter',
                          color: 'error.dark',
                        }}
                      />
                    )}
                  </Box>
                }
              />
            </Tabs>
          </Box>
        )}

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
      </Box>
        )}

        {!isLoading && !hasRequests && (
         
              <EmptyState
                icon={PendingActionsIcon}
                title="No Pairing Requests"
                description="You don't have any pairing requests at the moment. When Teachers submit pairing requests, they'll appear here for your review."
                iconClassName="request-icon"
                dotsClassName="request-dots"
              />
            
        )}

        {!isLoading && hasRequests && !hasFilteredRequests && (
          
              <EmptyState
                icon={PendingActionsIcon}
                title={`No ${selectedTab === 0 ? 'Pending' : selectedTab === 1 ? 'Accepted' : 'Rejected'} Requests`}
                description={selectedTab === 0
                  ? 'All requests have been reviewed. New pending requests will appear here.'
                  : selectedTab === 1
                    ? 'No accepted requests found. Accepted requests will appear here.'
                    : 'No rejected requests found. Rejected requests will appear here.'}
                iconClassName="request-icon"
                dotsClassName="request-dots"
              />
            
        )}

        {!isLoading && hasRequests && hasFilteredRequests && (
        <Stack spacing={3}>
            {filteredRequests.map((data, index) => {
              const isPending = data.status === 'PENDING';
              const isAccepted = data.status === 'ACCEPTED';
              const isRejected = data.status === 'REJECTED';

              return (
            <Card
              key={data?._id || index}
              sx={{
                borderRadius: 3,
                border: '1px solid',
                    borderColor: isPending
                      ? 'warning.light'
                      : isAccepted
                        ? 'success.light'
                        : 'error.light',
                boxShadow: (theme) => theme.customShadows?.card,
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? theme.palette.background.paper
                        : isPending
                          ? 'rgba(255, 193, 7, 0.04)'
                          : isAccepted
                            ? 'rgba(76, 175, 80, 0.04)'
                            : 'rgba(244, 67, 54, 0.04)',
              }}
            >
              <CardContent>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ md: 'flex-start' }}>
                  <Stack direction="row" spacing={2} flex={1}>
                        {/* Status Icon Avatar */}
                    <Avatar
                      variant="rounded"
                      sx={{
                            bgcolor: isPending
                              ? 'warning.lighter'
                              : isAccepted
                                ? 'success.lighter'
                                : 'error.lighter',
                            color: isPending
                              ? 'warning.dark'
                              : isAccepted
                                ? 'success.dark'
                                : 'error.dark',
                        width: 56,
                        height: 56,
                      }}
                    >
                          {isPending ? (
                            <PendingActionsIcon color="inherit" />
                          ) : isAccepted ? (
                            <CheckCircleIcon color="inherit" />
                          ) : (
                            <CancelIcon color="inherit" />
                          )}
                    </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap" sx={{ width: '100%' }}>
                            <Typography variant="subtitle2" sx={{ color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                        Request #{index + 1}
                      </Typography>
                            <Chip
                              label={isPending ? 'Pending' : isAccepted ? 'Accepted' : 'Rejected'}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                backgroundColor: isPending
                                  ? 'warning.lighter'
                                  : isAccepted
                                    ? 'success.lighter'
                                    : 'error.lighter',
                                color: isPending
                                  ? 'warning.dark'
                                  : isAccepted
                                    ? 'success.dark'
                                    : 'error.dark',
                              }}
                            />
                            {data?.timestamp && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                                <AccessTimeIcon sx={{ fontSize: '14px', color: 'text.secondary' }} />
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                  {format(new Date(data.timestamp), 'MMM dd, yyyy • hh:mm a')}
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                          <Typography variant="h6" sx={{ color: 'text.primary', mt: 0.5, fontWeight: 600 }}>
                        {data?.message}
                      </Typography>
                          

                          {/* User Information */}
                          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                            {/* Teacher/From User */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {data?.from ? (
                                <>
                                  <Avatar
                                    src={data.from.image ? `${data.from.image}?w=32&h=32&fit=crop&auto=format&q=80` : null}
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      bgcolor: 'primary.main',
                                      fontSize: '12px',
                                      fontWeight: 600,
                                      color: 'primary.contrastText',
                                    }}
                                  >
                                    {data.from.fname?.[0] || ''}{data.from.lname?.[0] || ''}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', display: 'block' }}>
                                      Teacher
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.85rem' }}>
                                      {data.from.fname} {data.from.lname}
                                    </Typography>
                                  </Box>
                                </>
                              ) : (
                                <>
                                  <Avatar
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      bgcolor: 'action.disabledBackground',
                                      fontSize: '12px',
                                      fontWeight: 600,
                                      color: 'text.secondary',
                                    }}
                                  >
                                    <PersonOffIcon sx={{ fontSize: '18px' }} />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', display: 'block' }}>
                                      Teacher
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.85rem', fontStyle: 'italic' }}>
                                      Deleted User
                                    </Typography>
                                  </Box>
                                </>
                              )}
                            </Box>

                            {/* Edifier/User */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {data?.user ? (
                                <>
                                  <Avatar
                                    src={data.user.image ? `${data.user.image}?w=32&h=32&fit=crop&auto=format&q=80` : null}
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      bgcolor: 'primary.main',
                                      fontSize: '12px',
                                      fontWeight: 600,
                                      color: 'primary.contrastText',
                                    }}
                                  >
                                    {data.user.fname?.[0] || ''}{data.user.lname?.[0] || ''}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', display: 'block' }}>
                                      Edifier
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.85rem' }}>
                                      {data.user.fname} {data.user.lname}
                                    </Typography>
                                  </Box>
                                </>
                              ) : (
                                <>
                                  <Avatar
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      bgcolor: 'action.disabledBackground',
                                      fontSize: '12px',
                                      fontWeight: 600,
                                      color: 'text.secondary',
                                    }}
                                  >
                                    <PersonOffIcon sx={{ fontSize: '18px' }} />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', display: 'block' }}>
                                      Edifier
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.85rem', fontStyle: 'italic' }}>
                                      Deleted User
                      </Typography>
                                  </Box>
                                </>
                              )}
                            </Box>
                          </Stack>
                    </Box>
                  </Stack>

                      {isPending && (
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}>
                    <Divider flexItem orientation="vertical" sx={{ display: { xs: 'none', md: 'block' } }} />
                    <Stack direction="row" spacing={1}>
                      <LoadingButton
                        variant="contained"
                        color="primary"
                        sx={{ minWidth: 120 }}
                        onClick={() => setPayload({ id: data?._id, status: 'ACCEPTED' })}
                        loading={isSiteLeaderRequestUpdating && data?._id === payload?.id && payload?.status === 'ACCEPTED'}
                      >
                        Approve
                      </LoadingButton>
                      <LoadingButton
                        variant="contained"
                        color="error"
                        sx={{ minWidth: 120 }}
                        onClick={() => setPayload({ id: data?._id, status: 'REJECTED' })}
                        loading={isSiteLeaderRequestUpdating && data?._id === payload?.id && payload?.status === 'REJECTED'}
                      >
                        Reject
                      </LoadingButton>
                    </Stack>
                  </Stack>
                      )}

                      {!isPending && (
                        <Box sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}>
                          <Chip
                            icon={isAccepted ? <CheckCircleIcon /> : <CancelIcon />}
                            label={isAccepted ? 'Approved' : 'Rejected'}
                            color={isAccepted ? 'success' : 'error'}
                            variant="outlined"
                            sx={{
                              fontWeight: 600,
                              minWidth: 120,
                            }}
                          />
                        </Box>
                      )}
                </Stack>
              </CardContent>
            </Card>
              );
            })}
        </Stack>
      )}
      </Container>
    </Box>
  );
};

export default SiteLeaderRequests;
